import React, { createContext, useContext, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gate } from '../utils/commandGate';
import { resolveHostContext } from '../utils/hostContext';

/**
 * THE PROGRAMMATIC HANDLE ON THE APPLICATION'S CHART CONTROLS.
 *
 * <h2>What this exists to fix</h2>
 *
 * The 2026-10-24 architecture audit found no store of any kind — no Redux, no Zustand, no
 * {@code createStore}, no {@code useReducer} — and 137 {@code useState} hooks inside a
 * 7,000-line {@code App.js}. Its setters are not exported, not on a ref, and reachable only as
 * props threaded into children. A Command Bus therefore had nothing to call.
 *
 * <p>This is the missing handle, built on the one working precedent in the codebase:
 * {@code ChartOverlayContext}, which already exposes {@code addOverlay} / {@code removeOverlay} /
 * {@code setAllOverlays} to anything under it.
 *
 * <h2>⚠ HANDLERS LIVE IN A REF, AND THE CONTEXT VALUE NEVER CHANGES IDENTITY</h2>
 *
 * This is the load-bearing decision and the reason the file looks the way it does.
 *
 * <p>{@code App} is BOTH the registrar and a child of this provider. Hold the registry in state
 * and {@code registerCommandHandlers} re-renders the provider, which re-renders {@code App},
 * whose registration effect can then run again — a loop, and an expensive one under a component
 * that rebuilds a chart option. Worse, a provider value rebuilt each render re-renders every
 * consumer, which here means the whole chart.
 *
 * <p>So the registry is a {@code useRef} and the value is a {@code useMemo} with an EMPTY
 * dependency list. Registering is a ref write: no render, anywhere. Every method below resolves
 * its handler at CALL time, so a stale value is impossible without a stale value ever being
 * captured.
 *
 * <h2>Every method answers, and a missing handler is a REFUSAL rather than silence</h2>
 *
 * {@code openDialog} and friends return {@code {ok, refusal?}}. The declared interface says
 * {@code void}, and a return value is strictly additive — a caller that ignores it is unchanged.
 * The alternative is a command that does nothing and says nothing, which is indistinguishable
 * from one that worked, and is the defect class this repository keeps recording.
 *
 * <h2>What it deliberately does NOT do</h2>
 *
 * No permission check. Whether a command is allowed is a question about the host, the role and
 * the toolbar profile ({@code utils/hostContext.js}, {@code config/nonAdminChrome.js},
 * {@code config/toolbarProfiles.js}), and it belongs in the gate that sits in FRONT of a
 * dispatcher rather than inside the plumbing every caller shares. A context that also policed
 * would be two jobs in one place and the policing would be bypassable by not using it.
 */

const CommandContext = createContext(null);

/** Why a command did not run. Returned rather than thrown: a refusal is an answer. */
export const REFUSAL = {
  /** `App` has not registered this handler. Either it is not wired yet, or App is unmounted. */
  NOT_REGISTERED: 'NOT_REGISTERED',
  /** The handler exists and rejected the arguments — an unknown dialog id, an invalid view. */
  INVALID: 'INVALID',
  /** The handler exists and the application is not in a state where it can run. */
  UNAVAILABLE: 'UNAVAILABLE',
};

/** Every handler name `App` may register. The single declaration of this context's surface. */
export const COMMAND_HANDLERS = [
  'setSymbol',
  'setChartType',
  'setMovingAverages',
  'toggleRSI',
  'toggleMACD',
  'openDialog',
  'closeDialog',
  'executeWithSymbol',
  'focusDateRange',
  'runStrategy',
  // ── the Command Bus needs four more, because a sentence can ask for them ───────────────────
  'runEngine',
  'runCycleBand',
  'runStudy',
  'clearOverlays',
  /*
   * ⚠ NOT A COMMAND — the GATE's context.
   *
   * `commandGate` needs `{isAdmin, profile}` and this provider knows neither: the role lives in
   * `App`'s auth state and the profile in its toolbar state. Registering it as a handler keeps
   * the bus ignorant of App's internals, which is the whole design of this file, and means a
   * caller never has to remember to pass a security context -- forgetting that is how a gate
   * ends up being asked the wrong question.
   */
  'gateContext',
];

/**
 * The commands that ALWAYS answer with a promise, registered or not.
 *
 * <p>⚠ Found by running it: a handler returning a promise was being wrapped as
 * {@code {ok: true, data: Promise}}, which is not a thenable — so {@code await setSymbol('QQQ')}
 * resolved in 0ms with the wrapper while the load it was supposed to wait for ran on unobserved.
 * It reported success and did nothing, which is the worst available shape for a command whose
 * entire purpose is to be awaited.
 *
 * <p>So a thenable is passed through untouched, and for these two a REFUSAL is a rejected
 * promise rather than an object: one return shape per command, always awaitable, never a value
 * a caller has to test before deciding whether they may await it.
 */
const ASYNC_COMMANDS = new Set(['setSymbol', 'executeWithSymbol', 'runStrategy',
  'runEngine', 'runCycleBand', 'runStudy']);

const isThenable = (v) => !!v && (typeof v === 'object' || typeof v === 'function')
  && typeof v.then === 'function';

const ok = (data) => (data === undefined ? { ok: true } : { ok: true, data });
const refuse = (code, message) => ({ ok: false, refusal: { code, message } });

export function CommandProvider({ children }) {
  /**
   * The registry. A REF, so registering renders nothing — see the note at the top of the file.
   */
  const handlersRef = useRef({});

  /**
   * ⚠ MERGES, never replaces.
   *
   * <p>A replacing register would mean one caller registering a subset silently removes
   * everything another registered — and the symptom is a command that worked a moment ago now
   * refusing with NOT_REGISTERED, which reads as a bug in the command rather than in the
   * registration. Merging also lets `App` register in pieces: the stable setters once at mount,
   * anything entitlement-dependent on its own effect.
   *
   * @returns {function} an unregister for exactly the keys this call supplied, so an effect can
   *                     clean up after itself without disturbing another registrar's keys.
   */
  const registerCommandHandlers = useMemo(() => (handlers) => {
    const incoming = handlers || {};
    const keys = Object.keys(incoming).filter(k => typeof incoming[k] === 'function');
    keys.forEach((k) => { handlersRef.current[k] = incoming[k]; });
    return () => {
      keys.forEach((k) => {
        // Only drop OUR function. A later registration of the same key has replaced it, and
        // removing that one would unregister a live handler on an unrelated unmount.
        if (handlersRef.current[k] === incoming[k]) delete handlersRef.current[k];
      });
    };
  }, []);

  /**
   * Call a registered handler, or refuse by name.
   *
   * <p>Resolved at CALL time rather than captured, which is what makes a frozen context value
   * safe: the value never changes and the behaviour is always current.
   */
  const call = useMemo(() => (name, ...args) => {
    const fn = handlersRef.current[name];
    if (typeof fn !== 'function') {
      const message = `No handler registered for "${name}". App.js registers these on mount.`;
      // An async command refuses as a REJECTED PROMISE, so every one of its answers is awaitable.
      return ASYNC_COMMANDS.has(name)
        ? Promise.reject(new Error(message))
        : refuse(REFUSAL.NOT_REGISTERED, message);
    }
    const result = fn(...args);
    // ⚠ A PROMISE IS RETURNED UNTOUCHED. Wrapping it made `await` resolve instantly on the
    // wrapper -- see the note on ASYNC_COMMANDS.
    if (isThenable(result)) return result;
    // A handler may answer with its own verdict; anything else counts as success.
    return (result && typeof result === 'object' && typeof result.ok === 'boolean')
      ? result
      : ok(result);
  }, []);

  /**
   * THE FROZEN VALUE.
   *
   * <p>Empty dependency list, deliberately and not by oversight. Nothing in it closes over
   * render-scoped state — every method reads `handlersRef` when it is called — so rebuilding it
   * could only ever cost renders and could never buy freshness.
   */
  /**
   * One command → one handler call.
   *
   * <p>A flat map rather than a switch, because the interesting property is COVERAGE: every
   * kind the Copilot can emit must be here, and a table can be compared against the backend's
   * tool list by a test. A switch's default case is a silent no-op wearing a success.
   *
   * <p>Each entry returns the ARGUMENTS for its handler, so the dispatch itself is one line and
   * cannot vary per kind. `null` means the payload was unusable — refused, never guessed at.
   */
  const dispatchOne = useMemo(() => {
    const ARGS = {
      'chart.setSymbol': (p) => (p.symbol ? ['executeWithSymbol', p.symbol, null] : null),
      'chart.setType': (p) => (p.view ? ['setChartType', p.view] : null),
      'chart.focusDateRange': (p) => ['focusDateRange', p.from ?? null, p.to ?? null],
      'chart.setMovingAverages': (p) => (Array.isArray(p.averages)
        ? ['setMovingAverages', p.averages] : null),
      'chart.toggleIndicator': (p) => {
        // One tool, two handlers. `on` defaults TRUE: "show me RSI" carries no boolean, and a
        // missing flag meaning "hide" would make the commonest phrasing do the opposite.
        const on = p.on === undefined ? true : !!p.on;
        if (p.indicator === 'rsi') return ['toggleRSI', on];
        if (p.indicator === 'macd') return ['toggleMACD', on];
        return null;
      },
      'overlay.clear': () => ['clearOverlays'],
      'engine.run': (p) => (p.provider ? ['runEngine', String(p.provider).toUpperCase()] : null),
      'engine.runCycleBand': (p) => (p.cycle ? ['runCycleBand', String(p.cycle).toLowerCase()] : null),
      /**
       * A config is passed as a SECOND argument, and ONLY when there is one.
       *
       * <p>`eclipse` and `pivotCommonalities` take none, so appending `undefined` for them would
       * change an existing call signature for no reason — caught by `commandBus.test.js`, which
       * asserts the exact args.
       *
       * <p>The rest-spread is tolerance for a model that puts `body1` at the top level instead of
       * under `config`: the schema asks for `config`, and a sentence that produced the flatter
       * shape would otherwise be refused for a reason the reader cannot see.
       */
      'study.run': (p) => {
        if (!p.study) return null;
        const { study, config, ...rest } = p;
        const cfg = config ?? (Object.keys(rest).length > 0 ? rest : null);
        return cfg ? ['runStudy', study, cfg] : ['runStudy', study];
      },
      'strategy.run': (p) => (p.designer ? ['runStrategy', p.designer, p.config || {}] : null),
      'dialog.open': (p) => (p.dialog ? ['openDialog', p.dialog, p.prefill] : null),
      'dialog.close': (p) => (p.dialog ? ['closeDialog', p.dialog] : null),
    };
    return (cmd) => {
      const build = ARGS[cmd.kind];
      if (!build) return { unknown: true };
      const args = build(cmd.payload || {});
      return args === null ? { invalid: true } : { args };
    };
  }, []);

  const executeOne = useMemo(() => async (cmd) => {
    const id = cmd?.id ?? null;
    if (!cmd || typeof cmd.kind !== 'string') {
      return { id, ok: false, refusal: { code: REFUSAL.INVALID, message: 'A command needs a kind.' } };
    }

    // The gate's context comes from App through a handler, never from the caller -- see the
    // note on `gateContext` in COMMAND_HANDLERS.
    const ctxFn = handlersRef.current.gateContext;
    const ctx = typeof ctxFn === 'function' ? (ctxFn() || {}) : {};
    const verdict = gate(cmd, { host: ctx.host || resolveHostContext(), ...ctx });
    if (!verdict.ok) return { id, ok: false, refusal: verdict.refusal };

    const resolved = dispatchOne(cmd);
    if (resolved.unknown) {
      return { id, ok: false,
        refusal: { code: REFUSAL.INVALID, message: `No command of kind "${cmd.kind}".` } };
    }
    if (resolved.invalid) {
      return { id, ok: false,
        refusal: { code: REFUSAL.INVALID, message: `"${cmd.kind}" was sent an unusable payload.` } };
    }

    try {
      // `call` already normalises: a thenable passes through, anything else is wrapped.
      const result = await call(...resolved.args);
      return result && result.ok === false
        ? { id, ok: false, refusal: result.refusal }
        : { id, ok: true, data: result?.data };
    } catch (e) {
      // An async handler REJECTS when it is unregistered or the run failed. A plan must report
      // that as a refusal rather than throwing into the UI, which has no error boundary here.
      return { id, ok: false,
        refusal: { code: REFUSAL.UNAVAILABLE, message: String(e?.message || e) } };
    }
  }, [call, dispatchOne]);

  const executeMany = useMemo(() => async (cmds) => {
    const list = Array.isArray(cmds) ? cmds : [cmds];
    const results = [];
    // The symbol first and AWAITED -- see the note on `executeAppCommands`.
    const ordered = [
      ...list.filter(c => c?.kind === 'chart.setSymbol'),
      ...list.filter(c => c?.kind !== 'chart.setSymbol'),
    ];
    for (const cmd of ordered) {
      // eslint-disable-next-line no-await-in-loop -- sequential BY DESIGN: the symbol's bars
      // must land before anything paints, and a later step may depend on an earlier one.
      const result = await executeOne(cmd);
      results.push(result);
      if (!result.ok) break;
    }
    return { ok: results.every(r => r.ok), results };
  }, [executeOne]);

  const value = useMemo(() => ({
    /* ── core chart controls ─────────────────────────────────────────────────────────────── */

    /**
     * Load a symbol, resolving once ITS bars are on the chart.
     *
     * <p>Asynchronous because the state change is only the start of it: `setSelectedSymbol`
     * kicks off a fetch and an overlay clear, and a caller that wants to draw afterwards must
     * wait for both. See `executeWithSymbol`, which this is the no-callback case of.
     */
    setSymbol: (symbol) => call('setSymbol', symbol),

    /**
     * ⚠ THE SAME STATE AS `setViewMode`, and the brief's three values are not the real ones.
     *
     * <p>There is exactly one view state in `App.js` — `chartType`, at line 925 — and
     * `utils/chartViewMode.js` defines what it may hold: `line`, `candlestick`, `hidden`. There
     * is no `bar` view and no second `viewMode` state to set. `hidden` is admin-only and is
     * normalised away for anyone else by `normalizeChartView`.
     */
    setChartType: (chartType) => call('setChartType', chartType),

    /**
     * Move the viewport to a date range. `yyyy-mm-dd`; either bound may be null for unbounded.
     *
     * <h2>⚠ THE ONE COMMAND THAT DOES NOT TOUCH REACT STATE, and must not</h2>
     *
     * The zoom window is held in `EChartComponent`'s `zoomRef`, deliberately outside React —
     * that component's own `datazoom` handler records why: setting state there "would trigger
     * memo recomputation -> notMerge:true full rebuild on every scroll, which corrupts the
     * chart". So this reaches the ECharts instance through the component's imperative handle
     * and dispatches, rather than going anywhere near a setter.
     *
     * <p>That is why it is the only chart command registered from a REF rather than from a
     * `useState` setter, and why the bounds SNAP INWARD: the handle resolves them through
     * `focusWindow`, the same helper the replay's year buttons use.
     *
     * @returns `{ok, data}` where data is `{from, to, startValue, endValue, bars}`, or a refusal
     *          when the range contains no bars, the dates are not `yyyy-mm-dd`, or no chart is
     *          mounted. Never a silent no-op: a viewport that did not move and said nothing is
     *          indistinguishable from one that moved to where it already was.
     */
    focusDateRange: (fromDate, toDate) => call('focusDateRange', fromDate, toDate),

    /**
     * An ALIAS of `setChartType`, pointing at the same handler and the same state.
     *
     * <p>Offered because the command interface names it, and implemented as an alias rather than
     * as a second handler so the two cannot drift into disagreeing about one piece of state.
     * `commandContext.test.js` pins that they call the same thing.
     */
    setViewMode: (viewMode) => call('setChartType', viewMode),

    /* ── indicators ──────────────────────────────────────────────────────────────────────── */

    setMovingAverages: (configs) => call('setMovingAverages', configs),

    /**
     * Show, hide, or flip.
     *
     * <p>`undefined` toggles and a boolean sets, which is what lets a Copilot say either "turn
     * RSI on" or "toggle RSI" without the caller having to read the current state first — and
     * reading it is not something a caller outside React can do.
     */
    toggleRSI: (show) => call('toggleRSI', show),
    toggleMACD: (show) => call('toggleMACD', show),

    /* ── strategies ──────────────────────────────────────────────────────────────────────── */

    /**
     * Run one of the four Strategy Designers WITHOUT opening it.
     *
     * <p>Audit blocker 4's other half. The payload builders are pure and shared with the
     * dialogs' own submit handlers, so a headless run and a button press send the same
     * request — two assemblies would agree on the day they were written and differ from the
     * first edit onward, and the difference would be a successful answer to a different
     * question.
     *
     * <p>It resolves with the backend's RESULT and paints nothing. Drawing needs `addOverlay`,
     * the journal and the messages panel; folding them in would make the numbers unobtainable
     * without also redrawing the chart.
     *
     * <p>⚠ Designer 3 is submit-then-poll and can take up to three minutes.
     *
     * @param designerId 1 Strategy Design · 2 Strategy 2 · 3 Time and Event · 4 MOA
     * @param config     that designer's form state
     * @returns a promise of the result; rejects on an unknown id, an unbuildable payload, or a
     *          failed request. Always a promise, registered or not.
     */
    runStrategy: (designerId, config) => call('runStrategy', designerId, config),

    /* ── dialogs ─────────────────────────────────────────────────────────────────────────── */

    /**
     * Open a dialog by id.
     *
     * <p>⚠ `prefill` is accepted by the dialogs that can HONOUR one and refused by the rest.
     * As of 2026-10-24 that is the four Strategy Designers (`strategyDesign`, `strategy2Design`,
     * `timeAndEventStrategy`, `moa`) and `visualUniverse`; every other dialog still holds its
     * form in local state from literal constants. Opening one of those and dropping the prefill
     * silently would leave the caller believing it had been applied, which is worse than not
     * opening at all — so it is refused with the reason. See `App.js` for the live list.
     */
    openDialog: (dialogId, prefill) => call('openDialog', dialogId, prefill),
    closeDialog: (dialogId) => call('closeDialog', dialogId),

    /* ── the symbol-change race ──────────────────────────────────────────────────────────── */

    /**
     * Load a symbol and run `afterBarsLoaded` only once its bars are on the chart.
     *
     * <p>This exists because of a documented trap at `App.js:4961`: changing the symbol fires an
     * effect that calls `clearOverlays()`, so an overlay added in the same tick is wiped a moment
     * later by the clear it had just triggered. Painting must wait for the new bars, and the
     * callback is invoked with them.
     *
     * @param {string} symbol
     * @param {function} [afterBarsLoaded] called with `{ symbol, barData, extendedBarData }`
     * @returns {Promise} resolves with the callback's result, rejects if the load fails
     */
    executeWithSymbol: (symbol, afterBarsLoaded) =>
      call('executeWithSymbol', symbol, afterBarsLoaded),

    /* ── registration and introspection ──────────────────────────────────────────────────── */

    registerCommandHandlers,

    /** Whether a given handler is wired. For a dispatcher that wants to refuse early. */
    isRegistered: (name) => typeof handlersRef.current[name] === 'function',

    /** The handlers currently registered, for diagnostics. */
    registeredCommands: () => Object.keys(handlersRef.current).sort(),

    /* ── THE COMMAND BUS ─────────────────────────────────────────────────────────────────── */

    /**
     * Gate one `AstroAppCommand`, then dispatch it.
     *
     * <p>⚠ THE GATE RUNS HERE EVEN THOUGH THE BACKEND ALREADY PRUNED ITS TOOL LIST. That
     * pruning is a measure against the model HALLUCINATING a call, and it keys on an `Origin`
     * header anyone can send. This is the enforcement, in the process that owns the state — and
     * a command does not have to come from the Copilot at all.
     *
     * @returns `{id, ok}` or `{id, ok: false, refusal: {code, message}}` — one shape, always.
     */
    executeAppCommand: (cmd) => executeOne(cmd),

    /**
     * Run a plan in order, stopping at the first refusal.
     *
     * <h2>⚠ A SYMBOL CHANGE IS AWAITED BEFORE ANYTHING ELSE RUNS</h2>
     *
     * "Show AAPL with RSI" is two commands, and the first of them fires a fetch AND an overlay
     * clear keyed on the symbol. Anything drawn before those land is wiped by an effect that
     * has nothing to do with it — the race `executeWithSymbol` exists for. So a plan containing
     * `chart.setSymbol` resolves it first and awaits the BARS, not the state change; the rest
     * then runs against a chart that is already showing the right instrument.
     *
     * <p>Stopping on the first refusal rather than pressing on: the commands in a plan are the
     * steps of one sentence, so a half-applied plan leaves the chart in a state the user did
     * not ask for and cannot name.
     */
    executeAppCommands: (cmds) => executeMany(cmds),
  }), [call, registerCommandHandlers, executeOne, executeMany]);

  /**
   * A DEVELOPMENT-ONLY HANDLE, so the Command Bus can be driven from a console while it is built.
   *
   * <p>Excluded from the production bundle by the environment check, which Terser removes
   * outright — `commandContext.test.js` pins that, because a global that reaches production is a
   * programmatic handle on the whole chart for anything running on the page.
   */
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    window.__astroCommands = value;
    return () => { delete window.__astroCommands; };
  }, [value]);

  return (
    <CommandContext.Provider value={value}>
      {children}
    </CommandContext.Provider>
  );
}

CommandProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * The command handle.
 *
 * <p>THROWS outside a provider rather than returning undefined, which is the one place this
 * differs from `useChartOverlays` beside it. That hook predates the convention and its callers
 * are all inside the provider by construction; a command handle will be reached from newer and
 * less predictable places, and `undefined.setSymbol` is a crash whose stack names neither the
 * caller nor the missing provider.
 */
export function useCommandContext() {
  const ctx = useContext(CommandContext);
  if (!ctx) {
    throw new Error(
      'useCommandContext must be used within a <CommandProvider>. It is mounted in App.js, '
      + 'inside ChartOverlayProvider — see SessionScope.');
  }
  return ctx;
}

export default CommandContext;
